import * as React from "react";
import Table from "@amzn/awsui-components-react-v3/polaris/table";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react-v3/polaris/space-between";
import TextFilter from "@amzn/awsui-components-react-v3/polaris/text-filter";
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import { User } from "../services/idp/idp.types";
import {
  Badge,
  Button,
  ButtonDropdown,
  CollectionPreferences,
  HelpPanel,
  Icon,
  Link,
  StatusIndicator,
} from "@amzn/awsui-components-react-v3";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BadgePhoto } from "./badge-photo";
import { UserStatusIndicator } from "./user-status-indicator";
import { LastAuthenticationBadge } from "./last-authentication-badge";
import { PagePaths, SupportUrls } from "../lib/constants";
import { Alerts } from "../lib/types";
import { formatRoute } from "../utils/route";
import {
  loadTablePreferences,
  saveTablePreferences,
  TablePreferencesConfig,
} from "../utils/table-preferences";

interface UserTableProps {
  users: User[];
  loading: boolean;
  showDisabled: boolean;
  onShowDisabledChange: (showDisabled: boolean) => void;
  onInfoClick: (panel: React.ReactNode) => void;
  setAlerts?: React.Dispatch<React.SetStateAction<Alerts>>;
}

const ColumnIds = {
  USER: "user",
  ALIAS: "alias",
  STATUS: "status",
  WEBAUTHN_CREDENTIALS: "webauthn-credentials",
  LAST_AUTH: "last-auth",
  OTP: "otp",
} as const;

const ActionIds = {
  TOGGLE_DISABLED: "toggle-disabled",
} as const;

const preferencesConfig: TablePreferencesConfig = {
  storageKey: "direct-reports-table-preferences",
  defaults: {
    contentDisplay: [
      { id: ColumnIds.USER, visible: true },
      { id: ColumnIds.ALIAS, visible: true },
      { id: ColumnIds.STATUS, visible: true },
      { id: ColumnIds.WEBAUTHN_CREDENTIALS, visible: true },
      { id: ColumnIds.LAST_AUTH, visible: true },
      { id: ColumnIds.OTP, visible: false },
    ],
  },
};

const helpPanel = (
  <HelpPanel header={<Header variant="h2">Direct Reports</Header>}>
    <p>
      This table shows a summary of your direct reports with active accounts in
      Frontier. You can filter users based on their name or alias and view their
      full profile by clicking on their name.
    </p>
    <Box variant="h5">Disabled users</Box>
    <p>
      If you do not see one of your direct reports, it might be because their
      account is disabled. To view disabled users who report to you, expand the
      Actions dropdown and click "Show disabled users". You can then hover over
      the user status indicator to view the reason and time they were disabled.
    </p>
    <p>
      If you feel as though a user is incorrectly marked as disabled, please{" "}
      <Link external href={SupportUrls.RE_ENABLE_USER_REQUEST}>
        open a ticket
      </Link>
      .
    </p>
    <Box variant="h5">Last seen</Box>
    <p>
      How long it has been since the last successful authentication request for
      the user. A typical authentication request provides 24 hours of access.
      You can hover over the badge to see the exact authentication time.
    </p>
    <Box variant="h5">Table preferences</Box>
    <p>
      You can customize the table columns by clicking the{" "}
      <Icon name="settings" /> icon. Any changes you make will be automatically
      saved and re-applied on future visits to this page.
    </p>
  </HelpPanel>
);

export const DirectsTable = ({
  users,
  loading,
  showDisabled,
  onShowDisabledChange,
  onInfoClick,
  setAlerts,
}: UserTableProps) => {
  const [userSearchFilter, setUserSearchFilter] = useState("");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(preferencesConfig.defaults);

  useEffect(() => {
    const { preferences: loaded, failed } =
      loadTablePreferences(preferencesConfig);
    setPreferences(loaded);
    if (failed) {
      setAlerts?.((prev) => ({
        ...prev,
        loadPreferencesWarning: {
          message: "Failed to load table preferences. Resetting to defaults.",
          ariaLabel: "Warning",
          type: "warning",
        },
      }));
    }
  }, [setAlerts, setPreferences]);

  const filteredUsers = useMemo(() => {
    if (users?.length && userSearchFilter.length) {
      const filter = userSearchFilter.toLocaleLowerCase();
      return users.filter(
        (item: User) =>
          item.displayName.toLocaleLowerCase().includes(filter) ||
          item.alias.toLocaleLowerCase().includes(filter),
      );
    }
    return users ?? [];
  }, [users, userSearchFilter]);

  return (
    <Table
      columnDefinitions={[
        {
          id: ColumnIds.USER,
          header: "User",
          cell: (user) => (
            <SpaceBetween direction="horizontal" size="s" alignItems={"center"}>
              <BadgePhoto alias={user.alias} size="2.5rem" />
              <Link
                href={formatRoute(PagePaths.USER_PROFILE, {
                  routeParams: { alias: user.alias },
                  queryParams: { ref: pathname },
                })}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(
                    formatRoute(PagePaths.USER_PROFILE, {
                      routeParams: { alias: user.alias },
                      queryParams: { ref: pathname },
                    }),
                  );
                }}
              >
                {user.displayName}
              </Link>
            </SpaceBetween>
          ),
          isRowHeader: true,
          minWidth: 180,
        },
        {
          id: ColumnIds.ALIAS,
          header: "Alias",
          cell: (user) => user.alias,
        },
        {
          id: ColumnIds.WEBAUTHN_CREDENTIALS,
          header: "Active FIDO2 credentials",
          cell: (user) => (
            <Badge data-testid={`credentials-count-${user?.alias}`}>
              {user.activeCredentialsCount}
            </Badge>
          ),
          maxWidth: 130,
        },
        {
          id: ColumnIds.STATUS,
          header: "Status",
          cell: (user) => <UserStatusIndicator user={user} />,
        },
        {
          id: ColumnIds.OTP,
          header: "OTP",
          cell: (user) => (
            <StatusIndicator
              data-testid={`otp-status-${user?.alias}`}
              type={(() => {
                if (!user.otpEnabled) return "stopped";
                if (user.otpPinSet) return "success";
                return "warning";
              })()}
            >
              {(() => {
                if (!user.otpEnabled) return "Disabled";
                if (user.otpPinSet) return "Enabled, PIN set";
                return "Enabled, PIN not set";
              })()}
            </StatusIndicator>
          ),
        },
        {
          id: ColumnIds.LAST_AUTH,
          header: "Last seen",
          cell: (user) => (
            <LastAuthenticationBadge
              time={user.lastAuthSuccess}
              userStatus={user.status}
            />
          ),
        },
      ]}
      columnDisplay={preferences.contentDisplay}
      items={filteredUsers}
      loading={loading}
      loadingText="Loading directs"
      sortingDisabled
      empty={`No ${showDisabled ? "" : "active "}users found`}
      footer={
        showDisabled && (
          <Box textAlign="center" color="text-status-inactive">
            Showing disabled users.{" "}
            <Button
              variant="inline-link"
              onClick={() => onShowDisabledChange(false)}
            >
              Hide
            </Button>
          </Box>
        )
      }
      filter={
        <TextFilter
          filteringText={userSearchFilter}
          filteringPlaceholder="Filter users"
          filteringAriaLabel="Filter users"
          filteringClearAriaLabel="Clear filter"
          onChange={({ detail }) => setUserSearchFilter(detail.filteringText)}
          data-testid={`direct-reports-text-filter`}
        />
      }
      header={
        <Header
          info={
            <Button
              variant="inline-link"
              onClick={() => onInfoClick(helpPanel)}
            >
              Info
            </Button>
          }
          actions={
            <ButtonDropdown
              items={[
                {
                  id: ActionIds.TOGGLE_DISABLED,
                  text: showDisabled
                    ? "Hide disabled users"
                    : "Show disabled users",
                },
              ]}
              onItemClick={({ detail }) => {
                switch (detail.id) {
                  case ActionIds.TOGGLE_DISABLED:
                    onShowDisabledChange(!showDisabled);
                    break;
                  default:
                    setAlerts?.((prev) => ({
                      ...prev,
                      invalidAction: {
                        message: "Invalid action.",
                        ariaLabel: "Error",
                        type: "error",
                      },
                    }));
                }
              }}
            >
              Actions
            </ButtonDropdown>
          }
        >
          Direct reports
        </Header>
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={preferences}
          onConfirm={({ detail }) => {
            setPreferences(detail);
            saveTablePreferences(preferencesConfig, detail);
          }}
          contentDisplayPreference={{
            title: "Column preferences",
            options: [
              { id: ColumnIds.USER, label: "User", alwaysVisible: true },
              { id: ColumnIds.ALIAS, label: "Alias" },
              { id: ColumnIds.STATUS, label: "Status" },
              {
                id: ColumnIds.WEBAUTHN_CREDENTIALS,
                label: "Active FIDO2 credentials",
              },
              { id: ColumnIds.LAST_AUTH, label: "Last seen" },
              { id: ColumnIds.OTP, label: "OTP" },
            ],
          }}
        />
      }
    />
  );
};
